import { get } from 'aws-amplify/api';
import { logger } from 'src/logger';
import { BaseballCardApiResponse, BaseballCardEmployee, FintechPapiEmployee } from './interfaces';
import { getEmployeeByLogin } from './papi-service';
import { BATCH_SIZE } from 'src/components/das-finsuite/baseball-cards/constants/BaseballCardConstants';
import { OrgChartEmployee } from 'src/components/das-finsuite/baseball-cards/types/orgChart.types';
import { transformToOrgChartEmployee } from 'src/components/das-finsuite/baseball-cards/utils/employeeTransformers';

const BATCH_DELAY_MS = 500;

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise with a timeout and optional abort signal support.
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMsg - Error message for timeout
 * @param signal - Optional AbortSignal for cancellation
 */
export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, errorMsg: string, signal?: AbortSignal): Promise<T> => {
  let timeoutId: NodeJS.Timeout;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new TimeoutError(errorMsg)), timeoutMs);
  });

  // If signal is provided, add abort handling
  const abortPromise = signal
    ? new Promise<T>((_, reject) => {
        if (signal.aborted) {
          const error = new Error('Request was cancelled');
          error.name = 'AbortError';
          reject(error);
        }
        signal.addEventListener(
          'abort',
          () => {
            const error = new Error('Request was cancelled');
            error.name = 'AbortError';
            reject(error);
          },
          { once: true }
        );
      })
    : null;

  const racingPromises = [promise.finally(() => clearTimeout(timeoutId)), timeoutPromise];
  if (abortPromise) {
    racingPromises.push(abortPromise);
  }

  return Promise.race(racingPromises);
};

/**
 * Transforms a FintechPapiEmployee to a BaseballCardEmployee
 */
export const transformToBaseballCardEmployee = (employee: FintechPapiEmployee): BaseballCardEmployee => ({
  login: employee.basicInfo.login || '',
  firstName: employee.basicInfo.firstName || '',
  lastName: employee.basicInfo.lastName || '',
  jobLevel: employee.job.jobLevel || '',
  jobTitle: employee.basicInfo.businessTitle || '',
  costCenterName: employee.basicInfo.costCenterName || '',
  city: employee.workLocation.city || '',
  country: employee.workLocation.country || ''
});

export const fetchEmployeesBatch = async (aliases: string[]): Promise<BaseballCardEmployee[]> => {
  const employeeList: BaseballCardEmployee[] = [];

  for (let i = 0; i < aliases.length; i += BATCH_SIZE) {
    const batch = aliases.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (alias) => {
        try {
          const response = await getEmployeeByLogin(alias, 'job');
          return response.employee;
        } catch (error) {
          logger.warn('Failed to fetch employee', {
            alias,
            error: error instanceof Error ? error.message : String(error)
          });
          return undefined;
        }
      })
    );

    results.forEach((employee) => {
      if (employee) {
        employeeList.push(transformToBaseballCardEmployee(employee));
      }
    });

    if (i + BATCH_SIZE < aliases.length) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  return employeeList;
};

/**
 * Checks if an AbortSignal has been aborted and throws if so.
 */
const checkAbortSignal = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    const error = new Error('Request was cancelled');
    error.name = 'AbortError';
    throw error;
  }
};

/**
 * Fetches employees in batches for the org chart, including direct reports data.
 * Returns a Map for efficient lookup by login.
 *
 * @param aliases - Array of employee aliases to fetch
 * @param onProgress - Optional callback to report progress (processed count)
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Map of login to OrgChartEmployee
 */
export const fetchEmployeesBatchForOrgChart = async (
  aliases: string[],
  onProgress?: (processed: number) => void,
  signal?: AbortSignal
): Promise<Map<string, OrgChartEmployee>> => {
  const employeeMap = new Map<string, OrgChartEmployee>();
  let processed = 0;

  for (let i = 0; i < aliases.length; i += BATCH_SIZE) {
    // Check if cancelled before starting each batch
    checkAbortSignal(signal);

    const batch = aliases.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (alias) => {
        try {
          // Check if cancelled before each individual request
          checkAbortSignal(signal);

          const response = await getEmployeeByLogin(alias, 'job,direct-reports', signal);
          if (response.employee) {
            const orgChartEmployee = transformToOrgChartEmployee(response.employee);
            return { alias, employee: orgChartEmployee };
          }
          return { alias, employee: undefined };
        } catch (error) {
          // Re-throw abort errors
          if (error instanceof Error && error.name === 'AbortError') {
            throw error;
          }
          logger.warn('Failed to fetch employee in batch for org chart', {
            alias,
            error: error instanceof Error ? error.message : String(error)
          });
          return { alias, employee: undefined };
        }
      })
    );

    // Check if cancelled after batch completes
    checkAbortSignal(signal);

    results.forEach(({ alias, employee }) => {
      if (employee) {
        employeeMap.set(alias, employee);
      }
    });

    processed += batch.length;
    onProgress?.(processed);

    if (i + BATCH_SIZE < aliases.length) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  return employeeMap;
};

export const getBaseballCardByLogin = async (login: string): Promise<BaseballCardApiResponse | null> => {
  try {
    const path = `/v1/baseball-card/login/${login.toLowerCase()}`;

    logger.debug('Fetching baseball card', { login, path });

    const response = await get({
      apiName: 'BASEBALL_CARD_API',
      path
    }).response;

    // Use text() to fully consume the ReadableStream before parsing
    // This prevents incomplete data issues with AWS Amplify v6
    const responseText = await response.body.text();
    const responseBody = JSON.parse(responseText) as BaseballCardApiResponse;

    return responseBody;
  } catch (error) {
    logger.warn('BASEBALL_CARD_API getBaseballCardByLogin failed', {
      error: error instanceof Error ? error.message : String(error),
      login
    });
    return null;
  }
};
